// Báscula por Web Serial (Chrome/Edge, sin QZ Tray). Enlace persistente + lectura continua.
const listeners = new Set();
let port = null, reader = null, running = false, pollTimer = null;
let last = { weight: null, raw: "", at: 0, stable: false };
const hist = [];

export const scaleSupported = () => typeof navigator !== "undefined" && "serial" in navigator;

export function onScale(cb) { listeners.add(cb); cb(last); return () => listeners.delete(cb); }
export const lastWeight = () => last;
export const scaleConnected = () => running;
function emit() { listeners.forEach((cb) => { try { cb(last); } catch { /* noop */ } }); }

// Extrae el peso de una trama: "ST,GS,  0.250kg" · "  1.235 kg" · "W 0250 g" · "0.500"
export function parseWeight(text, unit = "kg") {
  text = String(text).replace(/[\x00-\x1f\x7f]/g, " ");
  const m = [...String(text).matchAll(/(-?\d+(?:[.,]\d+)?)\s*(kg|g|lb)?/gi)];
  if (!m.length) return null;
  const [, num, u] = m[m.length - 1];
  let w = parseFloat(num.replace(",", "."));
  if (Number.isNaN(w)) return null;
  const uu = (u || unit || "kg").toLowerCase();
  if (uu === "g") w = w / 1000;
  if (uu === "lb") w = w * 0.453592;
  return +w.toFixed(3);
}

function pushWeight(w, raw) {
  hist.push(w); if (hist.length > 4) hist.shift();
  const stable = hist.length >= 3 && hist.every((x) => Math.abs(x - w) < 0.003);
  last = { weight: w, raw, at: Date.now(), stable };
  emit();
}

export function scaleErrorText(e) {
  const name = e?.name || ""; const msg = e?.message || String(e || "");
  if (name === "NotFoundError") return "No se seleccionó ningún puerto (cierra el cuadro solo cuando elijas la báscula).";
  if (name === "SecurityError") return "El navegador bloqueó el acceso al puerto. Abre el sistema en su propia pestaña/ventana (no dentro de otra página) y usa HTTPS.";
  if (name === "InvalidStateError") return "El puerto ya está abierto. Cierra QZ Tray u otro programa que use la báscula y reintenta.";
  if (name === "NetworkError" || /Failed to open/i.test(msg)) return "No se pudo abrir el puerto: otro programa lo está usando (QZ Tray, software de la báscula) o el cable está desconectado.";
  if (msg === "unsupported") return "Tu navegador no soporta Web Serial. Usa Chrome o Edge (o la app instalada).";
  return `Error de báscula: ${name ? name + " · " : ""}${msg}`;
}

export async function linkScale() {
  if (!scaleSupported()) throw new Error("unsupported");
  const p = await navigator.serial.requestPort();
  localStorage.setItem("scale_linked", "1");
  return p;
}

export async function unlinkScale() {
  await disconnectScale();
  try { if (port?.forget) await port.forget(); } catch { /* noop */ }
  localStorage.removeItem("scale_linked");
}

export async function grantedPort() {
  if (!scaleSupported()) return null;
  const ports = await navigator.serial.getPorts();
  return ports[0] || null;
}

// Opciones de conexión a partir de la estación configurada
export const SCALE_PROTOCOLS = [
  ["torrey", "Torrey (L-EQ, LSQ, PCR, MFQ…) · comando P"],
  ["rhino", "Rhino (BAR-8RS, BAPRE…) · comando P"],
  ["continuous", "Transmisión continua (la báscula envía sola)"],
  ["custom", "Personalizado (comando propio)"],
];
export function scaleOptsFor(st = {}) {
  const proto = st.scale_protocol || "torrey";
  let pollCmd = null;
  if (proto === "torrey" || proto === "rhino") pollCmd = "P";
  else if (proto === "custom") pollCmd = st.scale_poll_cmd || null;
  return { baud: st.scale_baud || 9600, unit: st.scale_unit || "kg", pollCmd, pollMs: 500 };
}

// opts: { baud, unit, pollCmd, pollMs }
export async function connectScale(opts = {}) {
  if (running) return true;
  const p = await grantedPort();
  if (!p) return false;
  port = p;
  if (port.readable) { running = true; }
  else await port.open({ baudRate: Number(opts.baud) || 9600, dataBits: 8, stopBits: 1, parity: "none", flowControl: "none" });
  running = true;
  try { await port.setSignals({ dataTerminalReady: true, requestToSend: true }); } catch { /* algunos adaptadores no lo soportan */ }
  const unit = opts.unit || "kg";
  if (opts.pollCmd) {
    const enc = new TextEncoder();
    const send = async () => { try { const w = port.writable.getWriter(); await w.write(enc.encode(opts.pollCmd.replace(/\\r/g, "\r").replace(/\\n/g, "\n"))); w.releaseLock(); } catch { /* noop */ } };
    send(); pollTimer = setInterval(send, Number(opts.pollMs) || 700);
  }
  (async () => {
    let buf = "", idle = null;
    const dec = new TextDecoder("latin1");
    const flush = () => { const t = buf.trim(); buf = ""; if (!t) return; const w = parseWeight(t, unit); if (w !== null) pushWeight(w, t); else { last = { ...last, raw: t, at: Date.now() }; emit(); } };
    while (running && port?.readable) {
      try {
        reader = port.readable.getReader();
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value);
          let idx;
          while ((idx = buf.search(/[\r\n\x03\x04]/)) >= 0) {
            const line = buf.slice(0, idx).trim(); buf = buf.slice(idx + 1);
            if (!line) continue;
            const w = parseWeight(line, unit);
            if (w !== null) pushWeight(w, line); else { last = { ...last, raw: line, at: Date.now() }; emit(); }
          }
          // Tramas sin terminador (algunas Torrey/Rhino): procesar tras 120 ms sin datos
          if (idle) clearTimeout(idle);
          if (buf) idle = setTimeout(flush, 120);
          if (buf.length > 64) flush();
        }
      } catch { /* desconexión: salir */ running = false; }
      finally { try { reader?.releaseLock(); } catch { /* noop */ } reader = null; }
    }
    running = false; emit();
  })();
  emit();
  return true;
}

export async function disconnectScale() {
  running = false;
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  try { await reader?.cancel(); } catch { /* noop */ }
  try { await port?.close(); } catch { /* noop */ }
  port = null; hist.length = 0;
  last = { weight: null, raw: "", at: 0, stable: false }; emit();
}

// Lectura puntual: espera hasta timeout una lectura > 0 (estable si es posible)
export function readOnce(timeoutMs = 4000) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    if (last.weight > 0 && Date.now() - last.at < 1500 && last.stable) return resolve(last.weight);
    const off = onScale((s) => {
      if (s.weight > 0 && s.at >= started && (s.stable || Date.now() - started > 1500)) { off(); resolve(s.weight); }
    });
    setTimeout(() => { off(); last.weight > 0 ? resolve(last.weight) : reject(new Error("timeout")); }, timeoutMs);
  });
}
